import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FaSyncAlt } from 'react-icons/fa';
import { format, subYears, subMonths, subDays } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const UserProfits = ({ userID }) => {
  const { t } = useTranslation();
  const [profits, setProfits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState(new Date()); // Default to today
  const [toDate, setToDate] = useState(new Date()); // Default to today
  const [period, setPeriod] = useState(0); // Default: All
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const periodOptions = [
    { label: t('all'), value: 0 },
    { label: t('annually'), value: 1 },
    { label: t('monthly'), value: 2 },
    { label: t('weekly'), value: 3 },
    { label: t('daily'), value: 4 },
  ];

  useEffect(() => {
    const today = new Date();
    if (period === 0) {
      setFromDate(today);
      setToDate(today);
    } else if (period === 1 || period === 2) {
      setFromDate(subYears(today, 1));
      setToDate(today);
    } else if (period === 3) {
      setFromDate(subMonths(today, 3));
      setToDate(today);
    } else if (period === 4) {
      setFromDate(subDays(today, 15));
      setToDate(today);
    }
  }, [period]);

  const fetchProfits = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        userid: userID,
        startdate: fromDate ? format(fromDate, 'yyyy-MM-dd') : '',
        Enddate: toDate ? format(toDate, 'yyyy-MM-dd') : '',
        kind: period,
      };

      const response = await axios.post(
        'https://copyapi.avangar.ir/api/forex/userprofits',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        }
      );

      if (response.data.Message) {
        setProfits([]);
        setError(t('noProfits') || response.data.Message);
        setSnackbarMessage(t('noProfits') || response.data.Message);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } else {
        setProfits(response.data || []);
        setSnackbarMessage(t('dataFetched') || 'Data fetched successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.Message ||
        (err.request ? t('networkError') || 'Network error' : t('error') || 'Failed to fetch profits');
      setError(errorMessage);
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfits();
  }, [userID, fromDate, toDate, period]);

  const handleRefresh = () => {
    fetchProfits();
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 2, position: 'relative' }}>
        {/* Panel 1: Filter */}
        <Paper
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1200,
            bgcolor: 'rgba(255,255,255,0.95)',
            py: { xs: 1, sm: 2 },
            px: { xs: 1, sm: 2 },
            boxShadow: '0 2px 8px rgba(0,0,0,0.1), 0 0 10px rgba(0,255,0,0.2)',
            borderRadius: 2,
            animation: 'slideDown 0.5s ease-out forwards',
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: '#1e3c72',
              fontWeight: 'bold',
              mb: 2,
              textAlign: 'center',
            }}
          >
            {t('profits') || 'Profits'}
          </Typography>
          <Grid container spacing={0.5} alignItems="center">
            <Grid item xs={12} sm={2.1}>
              <Box sx={{ width: '85%' }}>
                <DatePicker
                  label={t('from') || 'From'}
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  format="yyyy-MM-dd"
                  disabled={period === 0}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      size: 'small',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#2a5298' },
                          '&:hover fieldset': { borderColor: '#1e3c72' },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1e3c72',
                            boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                          },
                          height: '40px',
                          fontSize: '0.85rem',
                          padding: '0 8px',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.85rem',
                          transform: 'translate(14px, 10px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)',
                          },
                        },
                        '& .MuiInputAdornment-root': {
                          marginLeft: '4px',
                          '& .MuiButtonBase-root': {
                            padding: '0',
                            width: '20px',
                            height: '20px',
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: '1rem',
                          },
                        },
                      },
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={2.1}>
              <Box sx={{ width: '85%' }}>
                <DatePicker
                  label={t('to') || 'To'}
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  format="yyyy-MM-dd"
                  disabled={period === 0}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      size: 'small',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#2a5298' },
                          '&:hover fieldset': { borderColor: '#1e3c72' },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1e3c72',
                            boxShadow: '0 0 8px rgba(255,0,0,0.3)',
                          },
                          height: '40px',
                          fontSize: '0.85rem',
                          padding: '0 8px',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.85rem',
                          transform: 'translate(14px, 10px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)',
                          },
                        },
                        '& .MuiInputAdornment-root': {
                          marginLeft: '4px',
                          '& .MuiButtonBase-root': {
                            padding: '0',
                            width: '20px',
                            height: '20px',
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: '1rem',
                          },
                        },
                      },
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label={t('period') || 'Period'}
                value={period}
                onChange={(e) => setPeriod(Number(e.target.value))}
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '& fieldset': { borderColor: '#2a5298' },
                    '&:hover fieldset': { borderColor: '#1e3c72' },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1e3c72',
                      boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                    },
                    height: '40px',
                    fontSize: '0.85rem',
                    padding: '0 8px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.85rem',
                    transform: 'translate(14px, 10px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -6px) scale(0.75)',
                    },
                  },
                }}
              >
                {periodOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton
                onClick={handleRefresh}
                sx={{
                  color: '#1e3c72',
                  '&:hover': {
                    bgcolor: '#e0f7fa',
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <FaSyncAlt />
              </IconButton>
            </Grid>
          </Grid>
        </Paper>

        {/* Panel 2: List with Vertical Scroll */}
        <Paper
          sx={{
            maxHeight: profits.length > 2 ? '120px' : 'auto',
            overflowY: profits.length > 2 ? 'auto' : 'hidden',
            p: 2,
            bgcolor: 'rgba(255,255,255,0.95)',
            borderRadius: 2,
            mb: 2,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#1e3c72',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#2a5298',
            },
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress
                size={60}
                thickness={4}
                sx={{
                  color: 'transparent',
                  background: 'linear-gradient(90deg, #00ff00, #ff0000)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
            </Box>
          ) : error ? (
            <Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>
              {error}
            </Typography>
          ) : profits.length > 0 ? (
            profits.map((profit, index) => (
              <Box
                key={index}
                sx={{
                  p: 1,
                  borderBottom: '1px solid #e0e0e0',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Typography variant="body1" sx={{ color: '#1e3c72' }}>
                  {t('profit')} {index + 1}: {profit.ProfitDate} - Total: {profit.Total} (Win: {profit.TotalWin}, Loss: {profit.TotalLoss})
                </Typography>
              </Box>
            ))
          ) : (
            <Typography sx={{ mt: 2, textAlign: 'center', color: '#1e3c72' }}>
              {t('noProfits') || 'No profits available'}
            </Typography>
          )}
        </Paper>

        {/* Panel 3: Chart with Horizontal Scroll */}
        {profits.length > 0 && (
          <Paper
            sx={{
              p: 2,
              bgcolor: 'rgba(255,255,255,0.95)',
              borderRadius: 2,
              overflowX: profits.length > 4 ? 'auto' : 'hidden',
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#1e3c72',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#2a5298',
              },
            }}
          >
            <Box sx={{ minWidth: profits.length > 4 ? `${profits.length * 150}px` : 'auto' }}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={profits}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="ProfitDate" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Total" fill="#1e3c72" name={t('totalProfit') || 'Total Profit'} />
                  <Bar dataKey="TotalWin" fill="#00ff00" name={t('totalWin') || 'Total Win'} />
                  <Bar dataKey="TotalLoss" fill="#ff0000" name={t('totalLoss') || 'Total Loss'} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        )}

        {/* Snackbar for Messages */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{
            '& .MuiSnackbarContent-root': {
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              animation: 'fadeInDown 0.5s ease-out forwards',
            },
          }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{
              width: '100%',
              bgcolor: snackbarSeverity === 'success' ? '#e8f5e9' : '#ffebee',
              color: snackbarSeverity === 'success' ? '#2e7d32' : '#c62828',
              '& .MuiAlert-icon': {
                color: snackbarSeverity === 'success' ? '#4caf50' : '#d32f2f',
              },
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default UserProfits;